// src/components/crm/JobKanbanBoard.js

import React from 'react';

const JobKanbanBoard = ({ jobs, statuses, onCardClick, resources }) => {
    const statusColors = {
        'Pending':     'bg-orange-50',
        'Scheduled':   'bg-blue-50',
        'In Progress': 'bg-yellow-50',
        'Completed':   'bg-green-50',
        'Canceled':    'bg-red-50',
        'Default':     'bg-gray-50',
    };

    // r1-rec-kanban-display-c7f2b9e4
    // Accept both a Firestore Timestamp (real jobs) and a JS Date (projected
    // recurring cards carry startAt as a Date). Previously a bare ?.toDate check
    // returned '' for every projected occurrence, so its card showed "Start:" blank.
    const formatDate = (timestamp) => {
        const d = timestamp?.toDate ? timestamp.toDate()
                : (timestamp instanceof Date ? timestamp : null);
        return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric'}) : '';
    };

    const getCustomerName = (job) => {
        return job.customerDetails?.name || job.clientName || 'N/A';
    };

    return (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {statuses.map(status => (
                <div
                    key={status}
                    className={`rounded-lg p-4 ${statusColors[status] || statusColors['Default']}`}
                >
                    <h3 className="font-bold text-lg mb-4 text-gray-800">{status}</h3>
                    <div className="space-y-4 min-h-[200px]"> 
                        {jobs.filter(j => j.status === status).map(job => {
                            const assignedResourceNames = (job.assignedResources || [])
                                .map(resourceId => resources.find(r => r.id === resourceId)?.name) 
                                .filter(Boolean); 
                            
                            const assignedTeamMemberNames = job.assignedToNames || [];
                            const routeGroupName = job.assignmentMode === 'group' ? job.routeGroupName : null;

                            return (
                                <div
                                    key={job.id}
                                    onClick={() => onCardClick(job)} 
                                    className="bg-white p-3 rounded-md shadow-sm cursor-pointer hover:shadow-lg transition-shadow border border-gray-200 text-xs" 
                                >
                                    {/* r1-rec-kanban-display-c7f2b9e4 */}
                                    {job.isProjected && (
                                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700 mb-1">Recurring</span>
                                    )}
                                    <p className="font-semibold text-gray-800 mb-1">{job.title}</p>
                                    <p className="text-gray-600 mb-2">{getCustomerName(job)}</p>
                                    <p className="text-gray-500 mb-2">Start: {formatDate(job.startAt)}</p>
                                    {job.status === 'Pending' && (
                                        <p className="text-[10px] font-semibold text-red-500 mb-2">📞 Call to confirm</p>
                                    )} 
                                    
                                    {routeGroupName ? (
                                        <div className="mb-2">
                                            <p className="text-gray-500 font-medium">Team:</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-medium">
                                                    🚗 {routeGroupName}
                                                </span>
                                            </div>
                                        </div>
                                    ) : assignedTeamMemberNames.length > 0 && (
                                        <div className="mb-2"> 
                                            <p className="text-gray-500 font-medium">Team:</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {assignedTeamMemberNames.map((name, index) => (
                                                    <span key={index} className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-medium">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {assignedResourceNames.length > 0 && (
                                        <div className="mt-2 pt-1 border-t border-gray-100">
                                            <p className="text-gray-500 font-medium">Resources:</p>
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {assignedResourceNames.map((name, index) => (
                                                    <span key={index} className="px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded text-[10px] font-medium">
                                                        {name}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {jobs.filter(j => j.status === status).length === 0 && (
                            <div className="text-center text-sm text-gray-400 pt-10">No jobs in this status</div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default JobKanbanBoard;